import { useState, useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BlogHero from './components/BlogHero';
import BlogCard from './components/BlogCard';
import BlogSidebar from './components/BlogSidebar';
import BlogCTABar from './components/BlogCTABar';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';
import { supabase } from '@/lib/supabase';
import { useSEO } from '@/utils/seo';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image_url: string;
  category: string;
  author: string;
  published_at: string;
  is_published: boolean;
}

export default function BlogListPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, featured_image_url, category, author, published_at, is_published')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      setPosts(data || []);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const recentPosts = posts.slice(0, 5).map((p) => ({ title: p.title, slug: p.slug, published_at: p.published_at }));
  const categories: string[] = [...new Set(posts.map((p) => p.category).filter(Boolean))];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Blog | Addiction Treatment & Pharmacy Insights | Thornhill Drug-Mart',
    description: 'Read expert articles on addiction treatment, pharmacy care, harm reduction, and wellness from Thornhill Drug-Mart. Stay informed about methadone, Suboxone, naloxone, and more.',
    keywords: 'addiction treatment blog, pharmacy blog Ontario, methadone information, harm reduction articles, Thornhill Drug-Mart blog',
    canonical: `${siteUrl}/blog`,
    ogType: 'website',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar isScrolled={isScrolled} />

      <BlogHero
        title="Our Blog"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Blog' },
        ]}
      />

      {/* Main content area */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Scrollable main content */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-foreground-400 text-2xl"></i>
                </div>
                <h2 className="text-lg font-bold text-foreground-900 mb-2">No Posts Yet</h2>
                <p className="text-sm text-foreground-500">Check back soon for new articles.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.map((post) => (
                  <BlogCard
                    key={post.id}
                    title={post.title}
                    slug={post.slug}
                    excerpt={post.excerpt}
                    featured_image_url={post.featured_image_url}
                    category={post.category}
                    author={post.author}
                    published_at={post.published_at}
                  />
                ))}
              </div>
            )}
          </main>

          {/* Sticky sidebar (GSAP ScrollTrigger) */}
          <BlogSidebar recentPosts={recentPosts} categories={categories} />

        </div>
      </div>

      <BlogCTABar />
      <MobileStickyCTA />
      <Footer />
    </div>
  );
}